/**
 * 파일 종류 → 아이콘 + 색상 매핑 (chat-file-icons — 2026-07-01).
 *
 * 첨부/미리보기 UI 에서 파일 종류(확장자·MIME)에 맞는 lucide 아이콘 + 종류별 색상 클래스를
 * 고른다. 여러 컴포넌트(AttachmentList / ChatMediaPanel / 미리보기 트리거·헤더)가 공유하는 SSOT.
 * 색상은 CSS `.chat-file-icon--<category>` 로 정의(ChatPage.css) — currentColor 기반이라 boxed
 * 래퍼는 그 색의 옅은 tint 배경을 갖는다.
 */
import {
  File, FileText, FileImage, FileSpreadsheet, Presentation, FileCode, FileArchive, FileType, Globe,
} from '../icons';
import { isImageMime } from './attachments';
import type { PreviewKind } from './preview-doc';

/** lucide 아이콘 컴포넌트 타입(모든 아이콘 동일 시그니처). */
type IconComp = typeof FileText;

export type FileIconCategory = 'pdf' | 'excel' | 'word' | 'ppt' | 'image' | 'md' | 'html' | 'archive' | 'file';

const CATEGORY_ICON: Record<FileIconCategory, IconComp> = {
  pdf: FileType,
  excel: FileSpreadsheet,
  word: FileText,
  ppt: Presentation,
  image: FileImage,
  md: FileCode,
  html: Globe,
  archive: FileArchive,
  file: File,
};

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1) : '';
}

/** 파일명/MIME → 종류 카테고리. mime 우선 → 확장자 폴백 → 일반 파일. */
export function fileCategory(opts: { name?: string | null; mimeType?: string | null }): FileIconCategory {
  const mime = (opts.mimeType ?? '').toLowerCase().split(';')[0]!.trim();
  const ext = extOf((opts.name ?? '').toLowerCase());

  if (isImageMime(mime) || /^(png|jpe?g|gif|webp|bmp|svg|heic|heif|avif)$/.test(ext)) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.includes('spreadsheet') || mime.includes('ms-excel') || /^(xlsx?|csv)$/.test(ext)) return 'excel';
  if (mime.includes('presentation') || mime.includes('powerpoint') || /^pptx?$/.test(ext)) return 'ppt';
  if (mime.includes('wordprocessing') || mime === 'application/msword' || /^(docx?|hwp|hwpx|odt|rtf)$/.test(ext)) return 'word';
  if (mime.includes('markdown') || /^(md|markdown)$/.test(ext)) return 'md';
  // html — 확장자 우선(업로드 content-type 이 text/plain 으로 안전화될 수 있음). text/html·xhtml 폴백.
  if (mime === 'text/html' || mime.startsWith('application/xhtml') || /^(html?|xhtml)$/.test(ext)) return 'html';
  if (mime.includes('zip') || mime.includes('compressed') || /^(zip|7z|rar|tar|gz|tgz)$/.test(ext)) return 'archive';
  return 'file';
}

/**
 * 파일 종류를 **짧은 이름**으로 (2026-08-20).
 *
 * 첨부 칸에 원래 MIME 을 그대로 찍으면 OOXML 은
 * `application/vnd.openxmlformats-officedocument.wordprocessingml.document` 라 네 줄로 접힌다 —
 * 외부 메일을 가져오기 시작하자 첨부 칸이 이 글자로 무너졌다(사용자 지적).
 *
 * 확장자를 먼저 쓴다. 사람이 실제로 알아보는 이름이고 번역도 필요 없다("DOCX"·"HWP"·"PDF").
 * 이름에 확장자가 없으면 MIME 의 뒷부분을 쓰고, 그것도 없으면 'FILE'.
 */
export function fileTypeLabel(opts: { name?: string | null; mimeType?: string | null }): string {
  const ext = extOf((opts.name ?? '').toLowerCase());
  if (/^[a-z0-9]{1,5}$/.test(ext)) return ext.toUpperCase();
  const mime = (opts.mimeType ?? '').toLowerCase().split(';')[0]!.trim();
  // octet-stream 은 "모르는 덩어리" 라는 뜻이다 — 그걸 종류 이름처럼 보여주면 거짓말이 된다.
  if (mime === 'application/octet-stream' || mime === 'binary/octet-stream') return 'FILE';
  const sub = mime.split('/')[1] ?? '';
  // vnd.* 나 x-* 처럼 긴 것은 마지막 조각만 — 그래도 길면 자른다.
  const tail = sub.split('.').pop()?.replace(/^x-/, '') ?? '';
  if (tail.length > 0 && tail.length <= 12) return tail.toUpperCase();
  return 'FILE';
}

/** 파일명/MIME → 종류별 아이콘. */
export function fileIconFor(opts: { name?: string | null; mimeType?: string | null }): IconComp {
  return CATEGORY_ICON[fileCategory(opts)];
}

/** 파일명/MIME → 종류별 색상 클래스(아이콘 또는 boxed 래퍼에 부여). */
export function fileIconClass(opts: { name?: string | null; mimeType?: string | null }): string {
  return `chat-file-icon--${fileCategory(opts)}`;
}

// Record<PreviewKind, _> 라 PreviewKind 에 새 종류가 추가되면 여기서 컴파일 에러가 난다(누락 방지).
const KIND_CATEGORY: Record<PreviewKind, FileIconCategory> = {
  pdf: 'pdf',
  xlsx: 'excel',
  docx: 'word',
  pptx: 'ppt',
  markdown: 'md',
  html: 'html',
  // 한글 문서 — 위 fileCategory 의 확장자 표(docx?|hwp|hwpx|odt|rtf)와 같은 분류로 맞춘다.
  hwp: 'word',
};

/** 미리보기 종류(kind) → 아이콘 (미리보기 트리거·패널 헤더용). */
export function previewKindIcon(kind: PreviewKind): IconComp {
  return CATEGORY_ICON[KIND_CATEGORY[kind]];
}

/** 미리보기 종류(kind) → 색상 클래스. */
export function previewKindClass(kind: PreviewKind): string {
  return `chat-file-icon--${KIND_CATEGORY[kind]}`;
}
